<script setup>
import { ref, computed, watch, onMounted, nextTick, useSlots } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';

const props = defineProps({
  conversationLabels: {
    type: Array,
    required: true,
  },
});

const slots = useSlots();
const accountLabels = useMapGetter('labels/getLabels');

const activeLabels = computed(() => {
  const loadedLabels = accountLabels.value.filter(({ title }) =>
    props.conversationLabels.includes(title)
  );

  // A conversa pode chegar antes da lista de labels da conta. Mantém a tag
  // terminal do funil visível mesmo nesse intervalo de sincronização.
  if (
    props.conversationLabels.includes('venda-perdida') &&
    !loadedLabels.some(({ title }) => title === 'venda-perdida')
  ) {
    loadedLabels.push({
      id: 'fallback-venda-perdida',
      title: 'Venda perdida',
      description: null,
      color: '#DC2626',
    });
  }

  return loadedLabels;
});

const showAllLabels = ref(false);
const showExpandLabelButton = ref(false);
const labelPosition = ref(-1);
const labelContainer = ref(null);

const syncTerminalLabelClass = () => {
  const card = labelContainer.value?.closest('.acl-conversation-card');
  if (!card) return;

  card.classList.toggle(
    'acl-has-venda-perdida',
    props.conversationLabels.includes('venda-perdida')
  );
};

const computeVisibleLabelPosition = () => {
  const beforeSlot = slots.before ? 100 : 0;
  if (!labelContainer.value) {
    return;
  }

  const labels = Array.from(labelContainer.value.querySelectorAll('.label'));
  let labelOffset = 0;
  showExpandLabelButton.value = false;
  labels.forEach((label, index) => {
    labelOffset += label.offsetWidth + 8;

    if (labelOffset < labelContainer.value.clientWidth - beforeSlot) {
      labelPosition.value = index;
    } else {
      showExpandLabelButton.value = labels.length > 1;
    }
  });
};

watch(
  () => props.conversationLabels,
  () => {
    nextTick(() => {
      computeVisibleLabelPosition();
      syncTerminalLabelClass();
    });
  },
  { deep: true }
);

watch(activeLabels, () => {
  nextTick(() => {
    computeVisibleLabelPosition();
    syncTerminalLabelClass();
  });
});

onMounted(() => {
  nextTick(() => {
    computeVisibleLabelPosition();
    syncTerminalLabelClass();
  });
});

const onShowLabels = e => {
  e.stopPropagation();
  showAllLabels.value = !showAllLabels.value;
  nextTick(() => computeVisibleLabelPosition());
};
</script>

<template>
  <div ref="labelContainer" v-resize="computeVisibleLabelPosition">
    <div
      v-if="activeLabels.length || $slots.before"
      class="flex items-end flex-shrink min-w-0 gap-y-1"
      :class="{ 'h-auto overflow-visible flex-row flex-wrap': showAllLabels }"
    >
      <slot name="before" />
      <woot-label
        v-for="(label, index) in activeLabels"
        :key="label ? label.id : index"
        :title="label.title"
        :description="label.description"
        :color="label.color"
        variant="smooth"
        class="!mb-0 max-w-[calc(100%-0.5rem)]"
        small
        :class="{
          'invisible absolute': !showAllLabels && index > labelPosition,
        }"
      />
      <button
        v-if="showExpandLabelButton"
        :title="
          showAllLabels
            ? $t('CONVERSATION.CARD.HIDE_LABELS')
            : $t('CONVERSATION.CARD.SHOW_LABELS')
        "
        class="h-5 py-0 px-1 flex-shrink-0 mr-6 ml-0 rtl:ml-6 rtl:mr-0 rtl:rotate-180 text-n-slate-11 border-n-strong dark:border-n-strong"
        @click="onShowLabels"
      >
        <fluent-icon
          :icon="showAllLabels ? 'chevron-left' : 'chevron-right'"
          size="12"
        />
      </button>
    </div>
  </div>
</template>

<style>
.acl-conversation-card.acl-has-venda-perdida
  .acl-origem-row > span.bg-n-teal-9 {
  font-size: 0;
  background: #dc2626;
}

.acl-conversation-card.acl-has-venda-perdida
  .acl-origem-row > span.bg-n-teal-9::after {
  content: 'Venda Perdida';
  font-size: 12px;
}
</style>
